import axios from 'axios';
import { apiClient } from '@/api/client';

/**
 * Data import (patients first). Mirrors the API's
 * `src/data-import/domain/types.ts` and `data-import.service.ts#present`.
 */

export const PATIENT_FIELDS = [
  'fullName',
  'firstName',
  'middleName',
  'lastName',
  'phone',
  'alternatePhone',
  'email',
  'birthDate',
  'gender',
  'fileNumber',
  'address',
  'city',
  'nationalId',
  'nationality',
  'occupation',
  'referralSource',
  'emergencyContactName',
  'emergencyContactPhone',
  'guardianName',
  'guardianPhone',
  'registeredAt',
  'branch',
  'allergies',
  'notes',
] as const;

export type PatientField = (typeof PATIENT_FIELDS)[number];

export const INCOME_FIELDS = [
  'invoiceNumber',
  'invoiceDate',
  'dueDate',
  'patientName',
  'patientPhone',
  'patientFileNumber',
  'description',
  'procedureCode',
  'quantity',
  'unitPrice',
  'lineTotal',
  'discount',
  'tax',
  'amountPaid',
  'paymentMethod',
  'paymentDate',
  'currency',
  'branch',
  'notes',
] as const;
export type IncomeField = (typeof INCOME_FIELDS)[number];

export const EXPENSE_FIELDS = [
  'expenseDate',
  'dueDate',
  'category',
  'expenseType',
  'description',
  'supplier',
  'referenceNumber',
  'amount',
  'amountPaid',
  'paymentStatus',
  'paymentDate',
  'currency',
  'branch',
  'notes',
] as const;
export type ExpenseField = (typeof EXPENSE_FIELDS)[number];

/** Categories an expense import may write (materials are Inventory's). */
export const IMPORT_EXPENSE_CATEGORIES = [
  'RENT',
  'SALARY',
  'DOCTOR_COMMISSION',
  'LAB',
  'UTILITIES',
  'EQUIPMENT',
  'MARKETING',
  'MAINTENANCE',
  'INSURANCE',
  'SUBSCRIPTIONS',
  'OTHER',
] as const;

export type EntityType = 'patients' | 'income' | 'expenses';
/** Entity types that move money: account, currencies, rates. */
export const MONEY_ENTITIES: EntityType[] = ['income', 'expenses'];
export type AnyField = PatientField | IncomeField | ExpenseField;
export type MappingTarget = AnyField | 'extra' | 'ignore';
export type FieldMapping = Record<string, MappingTarget>;
export type DateOrder = 'DMY' | 'MDY' | 'YMD';
export type EncodingChoice =
  | 'auto'
  | 'utf-8'
  | 'windows-1256'
  | 'windows-1252'
  | 'utf-16le'
  | 'utf-16be';

export type JobStatus =
  | 'parsing'
  | 'mapping'
  | 'validating'
  | 'review'
  | 'committing'
  | 'completed'
  | 'rolling_back'
  | 'rolled_back'
  | 'failed'
  | 'cancelled';

export const BUSY_STATUSES: JobStatus[] = [
  'parsing',
  'validating',
  'committing',
  'rolling_back',
];

export interface ColumnProfile {
  key: string;
  header: string;
  index: number;
  samples: string[];
  filled: number;
  suggestion?: { target: MappingTarget; confidence: number } | null;
  /** 'balance' | 'age': recognised but not imported in this version. */
  notice?: string | null;
}

export interface ImportOptions {
  dateOrder: 'auto' | DateOrder;
  encoding: EncodingChoice;
  headerRow: number | null;
  sheet: string | null;
  delimiter: string | null;
  keepExtraColumns: boolean;
  presetName?: string | null;
  /** Income / expenses: the account the money moved through. */
  financialAccountId?: string | null;
  /** Money: what the file's amounts are in when a row does not say; null = each branch's currency. */
  currency?: string | null;
  /** Money: currency → units of the clinic's base per one unit, where the clinic has no rate. */
  exchangeRates?: Record<string, string>;
  /** Expenses: what a row that says nothing about payment means. */
  unstatedPaid?: 'paid' | 'unpaid' | null;
  /** Expenses: the user accepted that the account goes below zero. */
  allowOverdraft?: boolean;
  /** Expenses: category for rows that do not name one. */
  defaultCategory?: string | null;
}

export interface FileProfile {
  kind: 'csv' | 'xlsx';
  encoding: string;
  encodingSource: 'bom' | 'valid-utf8' | 'legacy' | 'mixed' | 'user' | 'xlsx';
  legacyLines: number;
  repairedCells: number;
  suspectCells: number;
  delimiter: string | null;
  headerRow: number;
  sheets: string[];
  sheet: string | null;
  dataRows: number;
  skippedEmptyRows: number;
  hasArabic: boolean;
}

export interface DateInference {
  field: AnyField;
  order: DateOrder;
  source: 'detected' | 'assumed' | 'user';
  evidence: {
    dmy: number;
    mdy: number;
    ymd: number;
    iso: number;
    serial: number;
    named: number;
    ambiguous: number;
    invalid: number;
  };
}

export interface ImportStats {
  total: number;
  ready: number;
  attention: number;
  duplicates: number;
  unresolved: number;
  warnings: number;
  created: number;
  merged: number;
  skipped: number;
  failed: number;
  rolledBack: number;
  kept?: number;
  errorParams?: Record<string, string | number>;
  /** Income: what the rows that will be written add up to, per currency, in its minor units. */
  money?: Array<{
    currency: string;
    rows: number;
    totalMinor: number;
    paidMinor: number;
  }>;
  /** Income: currencies some rows have no exchange rate for. */
  missingRates?: string[];
  /** Expenses: the paying account now and after the import (minor units). */
  account?: {
    id: string;
    name: string;
    currency: string;
    balanceMinor: number;
    afterMinor: number;
  } | null;
  dates: DateInference[];
}

export interface ImportJob {
  id: string;
  entityType: EntityType;
  status: JobStatus;
  pendingAction: string | null;
  fileName: string;
  fileSize: number;
  fileKind: 'csv' | 'xlsx';
  fileProfile: FileProfile | null;
  columns: ColumnProfile[];
  mapping: FieldMapping;
  options: ImportOptions;
  defaultBranchId: string | null;
  duplicateOfJobId: string | null;
  stats: ImportStats;
  progress: { done: number; total: number };
  error: string | null;
  missingRequired: string[];
  createdAt: string;
  updatedAt: string;
  committedAt: string | null;
  completedAt: string | null;
  rollbackUntil: string | null;
  rollbackOpen: boolean;
  rolledBackAt: string | null;
  limits: { maxRows: number; maxFileMb: number; rollbackHours: number };
}

export interface RowIssue {
  field: AnyField | 'row';
  code: string;
  severity: 'error' | 'warning';
  value?: string;
  params?: Record<string, string | number>;
}

export interface DuplicateInfo {
  kind: 'existing' | 'file';
  match: 'exact' | 'possible';
  on: Array<
    | 'phone'
    | 'name'
    | 'birthDate'
    | 'fileNumber'
    | 'invoiceNumber'
    | 'patient'
    | 'date'
    | 'amount'
  >;
  recordId?: string;
  recordLabel?: string;
  patientId?: string;
  patientName?: string;
  patientPhone?: string;
  patientCode?: string;
  patientBirthDate?: string | null;
  archived?: boolean;
  rowNumber?: number;
}

export type Resolution = 'create' | 'merge' | 'skip';

export interface NormalizedPatient {
  firstName?: string;
  middleName?: string | null;
  lastName?: string | null;
  displayName?: string;
  phone?: string;
  alternatePhone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  gender?: 'male' | 'female' | null;
  fileNumber?: string | null;
  address?: string | null;
  branchId?: string | null;
  allergies?: string[] | null;
  registeredAt?: string | null;
  extras?: Array<{ label: string; value: string }>;
  // Income rows (piasters):
  invoiceNumber?: string | null;
  invoiceDate?: string;
  patientLabel?: string | null;
  totalMinor?: number;
  paidMinor?: number;
  currencyCode?: string;
  rateToBase?: string;
  // Expense rows:
  expenseDate?: string;
  category?: string;
  supplier?: string | null;
  referenceNumber?: string | null;
  status?: 'UNPAID' | 'PARTIALLY_PAID' | 'PAID';
  // Income invoice lines: the head lists its rows; a line names its head.
  groupRows?: number[] | null;
  groupTotalMinor?: number | null;
  groupHeadRow?: number | null;
  [key: string]: unknown;
}

export type RowStatus =
  | 'ready'
  | 'error'
  | 'duplicate'
  | 'created'
  | 'merged'
  | 'skipped'
  | 'failed'
  | 'rolled_back';

export interface ImportRow {
  id: string;
  rowNumber: number;
  raw: Record<string, string>;
  overrides: Record<string, string>;
  normalized: NormalizedPatient;
  issues: RowIssue[];
  status: RowStatus;
  duplicate: DuplicateInfo | null;
  resolution: Resolution | null;
  allowedResolutions: Resolution[];
  patientId: string | null;
  error: string | null;
}

export type RowView =
  | 'all'
  | 'ready'
  | 'attention'
  | 'duplicates'
  | 'unresolved'
  | 'warnings'
  | 'created'
  | 'merged'
  | 'skipped'
  | 'failed'
  | 'rolled_back'
  | 'kept';

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RollbackCheck {
  canRollback: boolean;
  reason: 'not_completed' | 'expired' | 'nothing_removable' | null;
  until: string | null;
  removable: number;
  removableCreated?: number;
  removableMerged?: number;
  kept: Array<{
    patientId: string;
    name: string;
    edited: boolean;
    tables: string[];
    status: string;
  }>;
  keptTotal?: number;
  /** Money: how each account's balance moves if undone, in minor units. */
  balanceChanges?: Array<{ accountId: string; minor: number }>;
}

export interface ImportPreset {
  id: string;
  name: string;
  system: boolean;
  mapping: FieldMapping;
  lastUsedAt: string | null;
}

export interface PatientSource {
  jobId: string;
  fileName: string;
  rowNumber: number;
  status: 'created' | 'merged';
  importedAt: string;
  extras: Array<{ label: string; value: string }>;
}

/** The API's thrown body, which the error filter nests under `errors`. */
export interface ImportApiError {
  code?: string;
  message?: string;
  params?: Record<string, string | number>;
  job?: ImportJob;
}

export function importErrorOf(err: unknown): ImportApiError {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { errors?: ImportApiError; code?: string; message?: string }
      | undefined;
    if (data?.errors && typeof data.errors === 'object') return data.errors;
    if (data?.code) return { code: data.code, message: data.message };
  }
  return {};
}

export const importsApi = {
  async upload(
    entity: EntityType,
    file: File,
    opts: { allowDuplicate?: boolean; defaultBranchId?: string | null },
    onProgress?: (fraction: number) => void,
  ): Promise<ImportJob> {
    const form = new FormData();
    form.append('file', file);
    if (opts.allowDuplicate) form.append('allowDuplicate', 'true');
    if (opts.defaultBranchId)
      form.append('defaultBranchId', opts.defaultBranchId);
    const res = await apiClient.post(`/imports/${entity}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) =>
        e.total && onProgress?.(Math.min(1, e.loaded / e.total)),
    });
    return res.data;
  },

  async list(
    entity: EntityType,
    page: number,
    pageSize: number,
  ): Promise<Page<ImportJob>> {
    const res = await apiClient.get('/imports', {
      params: { entity, page, pageSize },
    });
    return res.data;
  },

  async active(): Promise<{ items: ImportJob[] }> {
    const res = await apiClient.get('/imports/active');
    return res.data;
  },

  async get(id: string): Promise<ImportJob> {
    const res = await apiClient.get(`/imports/${id}`);
    return res.data;
  },

  async rows(
    id: string,
    view: RowView,
    page: number,
    pageSize: number,
    q?: string,
  ): Promise<Page<ImportRow>> {
    const res = await apiClient.get(`/imports/${id}/rows`, {
      params: { view, page, pageSize, q: q || undefined },
    });
    return res.data;
  },

  async updateMapping(
    id: string,
    body: {
      mapping?: FieldMapping;
      options?: Partial<ImportOptions>;
      defaultBranchId?: string | null;
    },
  ): Promise<ImportJob> {
    const res = await apiClient.patch(`/imports/${id}/mapping`, body);
    return res.data;
  },

  async patchRow(
    id: string,
    rowId: string,
    overrides: Record<string, string>,
  ): Promise<{ row: ImportRow; stats: ImportStats }> {
    const res = await apiClient.patch(`/imports/${id}/rows/${rowId}`, {
      overrides,
    });
    return res.data;
  },

  async resolve(
    id: string,
    body: {
      rowIds?: string[];
      scope?: 'exact' | 'possible' | 'all';
      kind?: 'existing' | 'file';
      resolution: Resolution | null;
    },
  ): Promise<{ changed: number; stats: ImportStats }> {
    const res = await apiClient.post(`/imports/${id}/resolve`, body);
    return res.data;
  },

  async commit(id: string): Promise<ImportJob> {
    const res = await apiClient.post(`/imports/${id}/commit`, {});
    return res.data;
  },

  async rollbackCheck(id: string): Promise<RollbackCheck> {
    const res = await apiClient.get(`/imports/${id}/rollback`);
    return res.data;
  },

  async rollback(id: string): Promise<ImportJob> {
    const res = await apiClient.post(`/imports/${id}/rollback`, {});
    return res.data;
  },

  async cancel(id: string): Promise<ImportJob> {
    const res = await apiClient.post(`/imports/${id}/cancel`, {});
    return res.data;
  },

  async exportRows(
    id: string,
    view: RowView,
    lang: 'en' | 'ar',
  ): Promise<{ filename: string; content: string }> {
    const res = await apiClient.get(`/imports/${id}/export`, {
      params: { view, lang },
    });
    return res.data;
  },

  /** A working example file for this clinic, in the given language. */
  async template(
    entity: EntityType,
    lang: 'en' | 'ar',
  ): Promise<{ filename: string; content: string }> {
    const res = await apiClient.get('/imports/template', {
      params: { entity, lang },
    });
    return res.data;
  },

  async presets(entity: EntityType): Promise<{ items: ImportPreset[] }> {
    const res = await apiClient.get('/imports/presets', { params: { entity } });
    return res.data;
  },

  async savePreset(jobId: string, name: string): Promise<ImportPreset> {
    const res = await apiClient.post('/imports/presets', { jobId, name });
    return res.data;
  },

  async applyPreset(jobId: string, presetId: string): Promise<ImportJob> {
    const res = await apiClient.post(`/imports/${jobId}/preset`, { presetId });
    return res.data;
  },

  async deletePreset(id: string): Promise<void> {
    await apiClient.delete(`/imports/presets/${id}`);
  },

  async patientSource(patientId: string): Promise<{ items: PatientSource[] }> {
    const res = await apiClient.get(`/imports/patient/${patientId}/source`);
    return res.data;
  },
};

/** Save a server-built CSV (already carrying its BOM) as a download. */
export function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Where each entity's import pages live, and where its records are listed. */
export const ENTITY_ROUTES: Record<
  EntityType,
  { home: string; records: string }
> = {
  patients: {
    home: '/dashboard/patients/import',
    records: '/dashboard/patients',
  },
  income: {
    home: '/dashboard/finance/income/import',
    records: '/dashboard/finance/income',
  },
  expenses: {
    home: '/dashboard/finance/expenses/import',
    records: '/dashboard/finance/expenses',
  },
};
export const jobPath = (job: Pick<ImportJob, 'id' | 'entityType'>) =>
  `${ENTITY_ROUTES[job.entityType ?? 'patients'].home}/${job.id}`;
